import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
} from 'react'
import {useSelector} from 'react-redux'
import {useTranslation} from 'react-i18next'

import Toast from 'components/Basic/Toast'
import {FileDownloadIcon} from 'components'
import {selectUser} from 'features/User'
import {CASBService} from 'services/api'

type DownloadStatus = 'idle' | 'polling' | 'ready' | 'error'

type DownloadPollingContextValue = {
  startDownload: (platform: string) => void
  isDownloadingForPlatform: (platform: string) => boolean
}

const DEFAULT_POLL_INTERVAL_MS = 5000
const DEFAULT_EXPORT_FILENAME = 'casb-export.csv'

const DownloadPollingContext =
  createContext<DownloadPollingContextValue | null>(null)

type PlatformState = {
  intervalId: ReturnType<typeof setInterval> | null
  pollInFlight: boolean
  status: DownloadStatus
}

type DownloadPollingProviderProps = PropsWithChildren<{
  pollIntervalMs?: number
}>

export const DownloadPollingProvider = ({
  children,
  pollIntervalMs = DEFAULT_POLL_INTERVAL_MS,
}: DownloadPollingProviderProps) => {
  const user = useSelector(selectUser)
  const {t} = useTranslation('casb', {useSuspense: false})

  const platformStatesRef = useRef<Map<string, PlatformState>>(new Map())
  // stateVersion triggers re-renders when platform states change
  const [stateVersion, setStateVersion] = useState(0)

  const getOrCreatePlatformState = useCallback(
    (platform: string): PlatformState => {
      const existing = platformStatesRef.current.get(platform)
      if (existing) return existing

      const initial: PlatformState = {
        intervalId: null,
        pollInFlight: false,
        status: 'idle',
      }
      platformStatesRef.current.set(platform, initial)
      return initial
    },
    [],
  )

  const updatePlatformState = useCallback(
    (platform: string, partial: Partial<PlatformState>) => {
      const current = getOrCreatePlatformState(platform)
      platformStatesRef.current.set(platform, {...current, ...partial})
      setStateVersion(v => v + 1)
    },
    [getOrCreatePlatformState],
  )

  const clearIntervalForPlatform = useCallback((platform: string) => {
    const state = platformStatesRef.current.get(platform)
    if (state?.intervalId) {
      clearInterval(state.intervalId)
      platformStatesRef.current.set(platform, {...state, intervalId: null})
    }
  }, [])

  const triggerDownload = useCallback(
    (url: string, fileName: string = DEFAULT_EXPORT_FILENAME) => {
      const link = document.createElement('a')
      link.href = url
      link.download = fileName
      document.body.appendChild(link)
      link.click()
      link.remove()
    },
    [],
  )

  const handlePollForPlatform = useCallback(
    async (provider: string) => {
      const state = getOrCreatePlatformState(provider)

      // Skip if not polling or if a poll is already in flight
      if (state.status !== 'polling' || state.pollInFlight) return

      updatePlatformState(provider, {
        pollInFlight: true,
      })

      try {
        const response = await CASBService.pollCASBExport({provider})

        if (response.status === 'completed' && response.downloadUrl) {
          clearIntervalForPlatform(provider)
          triggerDownload(response.downloadUrl, DEFAULT_EXPORT_FILENAME)
          updatePlatformState(provider, {status: 'ready', pollInFlight: false})
        } else if (response.status === 'failed') {
          clearIntervalForPlatform(provider)
          Toast({
            message: t('EXPORT_FAILED', {provider}),
            type: 'error',
          })
          updatePlatformState(provider, {status: 'error', pollInFlight: false})
        } else {
          // Still pending
          updatePlatformState(provider, {pollInFlight: false})
        }
      } catch {
        clearIntervalForPlatform(provider)
        Toast({
          message: t('EXPORT_FAILED', {provider}),
          type: 'error',
        })
        updatePlatformState(provider, {status: 'error', pollInFlight: false})
      }
    },
    [
      clearIntervalForPlatform,
      getOrCreatePlatformState,
      t,
      triggerDownload,
      updatePlatformState,
    ],
  )

  const startDownload = useCallback(
    async (provider: string) => {
      if (!provider) {
        Toast({
          message: t('EXPORT_FAILED', {provider: 'Unknown'}),
          type: 'error',
        })
        return
      }

      const state = getOrCreatePlatformState(provider)

      // If already polling for this platform, ignore duplicate clicks
      if (state.status === 'polling') return

      // Clear any existing interval and reset state
      clearIntervalForPlatform(provider)
      updatePlatformState(provider, {
        status: 'polling',
        pollInFlight: false,
      })

      // Show success message immediately
      Toast({
        message: t('EXPORT_DOWNLOAD_IN_PROGRESS'),
        type: 'info',
        icon: <FileDownloadIcon />,
      })

      try {
        await CASBService.startCASBExport({
          provider,
        })

        // Start polling
        const intervalId = setInterval(() => {
          handlePollForPlatform(provider)
        }, pollIntervalMs)

        updatePlatformState(provider, {intervalId})

        // Initial poll
        handlePollForPlatform(provider)
      } catch {
        Toast({
          message: t('EXPORT_FAILED', {provider}),
          type: 'error',
        })
        updatePlatformState(provider, {status: 'error', pollInFlight: false})
      }
    },
    [
      clearIntervalForPlatform,
      getOrCreatePlatformState,
      handlePollForPlatform,
      pollIntervalMs,
      t,
      updatePlatformState,
    ],
  )

  // Internal cleanup function - stops all platform downloads
  const stopAll = useCallback(() => {
    platformStatesRef.current.forEach((_, platform) => {
      clearIntervalForPlatform(platform)
    })
    platformStatesRef.current.clear()
    setStateVersion(v => v + 1)
  }, [clearIntervalForPlatform])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      platformStatesRef.current.forEach(state => {
        if (state.intervalId) {
          clearInterval(state.intervalId)
        }
      })
      platformStatesRef.current.clear()
    }
  }, [])

  // Stop all downloads when user logs out
  const userToken = user?.current?.token
  useEffect(() => {
    if (!userToken) {
      stopAll()
    }
  }, [userToken, stopAll])

  const isDownloadingForPlatform = useCallback(
    (platform: string) =>
      platformStatesRef.current.get(platform)?.status === 'polling',
    [stateVersion], // eslint-disable-line react-hooks/exhaustive-deps
  )

  const value = useMemo(
    () => ({
      startDownload,
      isDownloadingForPlatform,
    }),
    [startDownload, isDownloadingForPlatform],
  )

  return (
    <DownloadPollingContext.Provider value={value}>
      {children}
    </DownloadPollingContext.Provider>
  )
}

export const useDownloadPollingContext = (): DownloadPollingContextValue => {
  const ctx = useContext(DownloadPollingContext)
  if (!ctx) {
    throw new Error(
      'useDownloadPollingContext must be used within DownloadPollingProvider',
    )
  }
  return ctx
}
